import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Add } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { SmallAddButton } from '../Primitives';
import { ContactFilesData, useContactFiles } from '../../data/useContactFiles';
import { createFileDropHandler } from '../../../../api/files';
import { FormGrid, LoadingIndicator, MoreActionsMenuButton, PseudoLink2, SimpleDialog } from '../../../primitives';
import { FormControlsForFields } from '../../../schemed';

const FilesContainer = styled.div<{ isDragTarget?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 3px dashed ${props => props.isDragTarget ? props.theme.palette.secondary.main : 'transparent'};
  border-radius: 8px;
  max-width: 100%;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 2px 0;
  border-bottom: 1px solid ${props => props.theme.palette.divider};

  &:last-of-type {
    border-bottom: none;
  }

  & .actions {
    flex-shrink: 0;
  }
`;

const FileMain = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 4px;

  & .filename {
    display: block;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }
`;


const FileComment = styled.div`
  color: ${props => props.theme.palette.text.secondary};
  white-space: pre-wrap;
  word-break: break-word;

  font-size: 0.75rem;
  line-height: 1.1;
`;


interface Props {
  data: ContactFilesData;
}

export const ContactFilesSection = ({ data }: Props) => {
  const [isDragTarget, setIsDragTarget] = useState(false);

  return (
    <FilesContainer
      isDragTarget={isDragTarget}
      onDragEnter={e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragTarget(true);
      }}
      onDragOver={e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragTarget(true);
      }}
      onDragLeave={() => setIsDragTarget(false)}
      onDrop={createFileDropHandler(f => {
        setIsDragTarget(false);
        data.upload(f);
      }, true)}
    >
     

      {!!data.data.length &&
        <FilesList>
          {data.data.map(file => (
            <FileRow key={file._id} style={!file.comment ? { alignItems: "center" } : undefined}>
              <FileMain>
                <PseudoLink2 className="filename" onClick={() => data.download(file.fileinfo)}>
                  {file.filename}
                </PseudoLink2>
                {!!file.comment &&
                  <FileComment>{file.comment}</FileComment>}
              </FileMain>

              <MoreActionsMenuButton
                strippedButton
                className="actions"
                size="small"
                actions={[
                  ["edit", "Комментарий", () => data.editItem.startEditing(file)],
                  ["delete", <FormattedMessage id="common.delete" />, () => data.removeItem.run(file.fileinfo)],
                ]}
                />
            </FileRow>
          ))}
        </FilesList>}

       <HeaderRow>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {data.isLoading && <LoadingIndicator sizeVariant="s" />}
        </div>

        <SmallAddButton icon={<Add />} onClick={() => data.create()}>
          добавить файл
        </SmallAddButton>
      </HeaderRow>

      <SimpleDialog
        dialogTitle="Файл"
        {...data.editItem.dialog}
        noFullscreen
        submitOnModEnter
        save={() => data.editItem.save()}>
        {!!data.editItem.item &&
          <FormGrid columns="1fr">
            <FormControlsForFields
              fields={[
                ["filename", { disabled: true }],
                ["comment", { controlProps: { autoFocus: true }}],
              ]}
              schema={data.schema}
              data={data.editItem.item}
              onChange={(o,c) => data.editItem.update(c)}
              />
          </FormGrid>}
      </SimpleDialog>
    </FilesContainer>
  );
};

export const ContactFilesSectionBound = (props: { contactId?: string }) => {
  const data = useContactFiles(props.contactId);

  return (
    <ContactFilesSection data={data} />
  );
};